import React from 'react';
import classnames from 'classnames';
import Icon from '../common/Icon';
import { baseXUnit, breakpoints, color, spacing } from '../../styles/theme';
import ButtonText from '../elements/ButtonText';
import { getAnswerOptionLabel } from '../../search/SearchAnswers';
import { Question } from '../../redux/features/questions';
import DivButton from '../a11y/DivButton';

interface SearchResultsFilterButtonProps {
  question: Question;
  currentAnswerOption?: string;
  onClick: () => void;
}

const SearchResultsFilterButton: React.FunctionComponent<
  SearchResultsFilterButtonProps
> = ({ question, currentAnswerOption, onClick, ...rest }) => {
  const questionAnswered = !!currentAnswerOption;
  const answerOptionLabel =
    currentAnswerOption && getAnswerOptionLabel(question, currentAnswerOption);
  return (
    <div
      className="search-results-filter-button"
      data-testid="search-results-filter-button"
      data-cy="search-results-filter-button"
      {...rest}
    >
      <DivButton onClick={onClick}>
        <div
          className={classnames('content', {
            'no-answer': !questionAnswered
          })}
        >
          {!questionAnswered && (
            <div className="no-answer-icon" data-testid="no-answer-icon">
              <Icon name="nav-expand" size={2} color="purple-light" />
            </div>
          )}
          <ButtonText
            variant="primary"
            color={questionAnswered ? 'white' : 'dark'}
          >
            <div className="text">
              {questionAnswered && answerOptionLabel
                ? answerOptionLabel
                : question.shortQuestionText}
            </div>
          </ButtonText>
          {questionAnswered && (
            <div className="icon" data-testid="question-answered-icon">
              <Icon name="nav-kebab" size={2} color="white" />
            </div>
          )}
        </div>
      </DivButton>
      {/*language=CSS*/}
      <style jsx>{`
        .search-results-filter-button {
          display: inline-block;
          margin-right: ${baseXUnit(1)};
          vertical-align: top;
        }
        .content {
          border-radius: ${baseXUnit(1)} ${baseXUnit(1)} 0 ${baseXUnit(1)};
          background: ${color.purpleLight};
          border: 1px solid transparent;
          padding: ${spacing.fourth} ${spacing.half} ${spacing.fourth}
            ${spacing.one};
          display: flex;
          transition: 0.5s;
        }
        .content:hover {
          background: ${color.purple};
          box-shadow: 0 ${baseXUnit(0.2)} ${baseXUnit(2)} 0
            rgba(124, 34, 195, 0.6);
        }
        .content:active {
          background: ${color.pool};
          box-shadow: none;
        }
        .content.no-answer {
          padding: ${spacing.fourth} ${spacing.one} ${spacing.fourth}
            ${spacing.half};
          border-radius: ${baseXUnit(1)};
          background-color: ${color.lightBackground};
        }
        .content.no-answer:hover {
          background-color: ${color.light};
          box-shadow: none;
        }
        .content.no-answer:active {
          background-color: ${color.pool};
        }
        .text {
          padding-top: ${spacing.fourth};
        }
        .icon {
          margin-left: ${spacing.half};
          display: flex;
          align-items: center;
          opacity: 0.7;
        }
        .no-answer-icon {
          display: flex;
          align-items: center;
          margin-right: ${spacing.half};
        }

        @media ${breakpoints.large} {
          .search-results-filter-button {
            margin-right: ${baseXUnit(2)};
            margin-bottom: ${baseXUnit(2)};
          }
        }
      `}</style>
    </div>
  );
};

export default SearchResultsFilterButton;
